import { useState, useCallback } from 'react'
import { layout } from 'amhm-vc-ui-kit'
import { getLegacyPlatformIdFromSessionRouting } from 'src/live'
import { 
  getAgoraToken, 
  getAgoraChatToken,
  markBookedEventLog 
} from 'src/consumer/services/featureServices'
import { generateUidForAgora } from 'src/consumer/helpers/utilFunctions'
import { allAvailableEventTypes } from 'src/consumer/constants'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { buildPatientRtmUsername } from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/utils/rtmUsernameBuilder'

export const useAgoraConnection = () => {
  const [agoraRtcProps, setAgoraRtcProps] = useState({})
  const [agoraRtmProps, setAgoraRtmProps] = useState({})
  const [uidObject, setUidObject] = useState({})
  
  // Chat related
  const [myChatId, setMyChatId] = useState()
  const [myChatToken, setMyChatToken] = useState()
  const [peerChatId, setPeerChatId] = useState()
  const [peerChatName, setPeerChatName] = useState()
  const [peerChatImage, setPeerChatImage] = useState()

  const initializeUidObject = useCallback(() => {
    setUidObject({
      uid: generateUidForAgora(),
      uidScreen: generateUidForAgora(),
      uidRecording: generateUidForAgora(),
    })
  }, [])

  const handleJoinRoom = useCallback(async ({
    bookedEventFinalData,
    userRole,
    username,
    userHasAllowedAudio,
    userHasAllowedVideo,
    selectedCamera,
    selectedMicrophone,
    setTokenLoading,
    setSessionStarted,
    setIsCameraOpen,
    setIsAudioOpen,
    selectedPlatform,
    patientInfo = null,
    userProfileData = null,
  }) => {
    console.log('=== useAgoraConnection.handleJoinRoom CALLED ===', {
      hasBookedEventFinalData: !!bookedEventFinalData,
      hasSelectedPlatform: !!selectedPlatform,
      hasUserRole: !!userRole,
      bookedEventFinalData,
      selectedPlatform,
      userRole,
    })
    
    // Defensive checks
    if (!bookedEventFinalData) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] bookedEventFinalData is required')
      return { success: false, error: 'bookedEventFinalData is required' }
    }

    const resolvedSelectedPlatform =
      selectedPlatform ||
      (bookedEventFinalData
        ? {
            id: getLegacyPlatformIdFromSessionRouting(
              bookedEventFinalData?.sessionRoutingDecision,
              bookedEventFinalData?.legacyPlatformId || bookedEventFinalData?.meetingSrc,
            ),
          }
        : null)
    
    if (!resolvedSelectedPlatform?.id) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] selectedPlatform is required', {
        meetingSrc: bookedEventFinalData?.meetingSrc,
        bookedEventFinalData,
        resolvedPlatformId: resolvedSelectedPlatform?.id,
      })
      return { success: false, error: 'selectedPlatform is required' }
    }
    
    if (!userRole) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] userRole is required')
      return { success: false, error: 'userRole is required' }
    }
    
    console.log('✅ [useAgoraConnection.handleJoinRoom] All checks passed. Starting join process:', {
      platform: resolvedSelectedPlatform?.id,
      eventId: bookedEventFinalData?.id,
      eventType: bookedEventFinalData?.eventType,
      userRole,
      meetingSrc: bookedEventFinalData?.meetingSrc,
    })
    
    if (resolvedSelectedPlatform?.id === 'aumhum') {
      setTokenLoading(true)
      let response
      let responseScreen
      let role = 'host'
      
      if (userRole === 'mentor') {
        response = await getAgoraToken(bookedEventFinalData?.id, true)
        responseScreen = await getAgoraToken(bookedEventFinalData?.id, true)
      } else {
        if (bookedEventFinalData?.eventType === allAvailableEventTypes.webinar) {
          role = 'audience'
        }
        response = await getAgoraToken(bookedEventFinalData?.id, false)
        responseScreen = await getAgoraToken(bookedEventFinalData?.id, false)
      }
      
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.userName &&
        response?.data?.data?.rtcToken &&
        response?.data?.data?.rtmToken &&
        responseScreen?.status === 200 &&
        isCommonResponseSuccessful(responseScreen?.data?.code) &&
        responseScreen?.data?.data?.userName &&
        responseScreen?.data?.data?.rtcToken &&
        responseScreen?.data?.data?.rtmToken
      ) {
        setAgoraRtcProps({
          appId: import.meta.env.VITE_AGORA_APP_ID,
          channel: bookedEventFinalData?.id,
          uid: parseInt(response?.data?.data?.userName),
          token: response?.data?.data?.rtcToken,
          enableScreensharing: true,
          screenshareUid: responseScreen?.data?.data?.userName,
          screenshareToken: responseScreen?.data?.data?.rtcToken,
          layout: layout.pin,
          role,
          enableAudio: userHasAllowedAudio,
          enableVideo: userHasAllowedVideo,
          cameraDeviceId: selectedCamera,
          microphoneDeviceId: selectedMicrophone,
        })
        
        // Build RTM username with role, name, and profilePicId
        // For patients/endUsers, use patientInfo if available
        let rtmUsername = username
        if (userRole !== 'mentor' && userRole !== 'panelist' && (patientInfo || userProfileData)) {
          const patientInfoObj = patientInfo || { name: username }
          rtmUsername = buildPatientRtmUsername(patientInfoObj, userProfileData, username)
        }
        
        // Parse to show the object being sent (for validation, not logging)
        let parsedUsername = null
        try {
          parsedUsername = JSON.parse(rtmUsername)
        } catch (error) {
          // If not JSON, it's a plain string (legacy format)
          parsedUsername = { raw: rtmUsername }
        }
        
        // Debug logging removed to prevent continuous logging loops
        
        setAgoraRtmProps({
          username: rtmUsername,
          displayUsername: true,
          token: response?.data?.data?.rtmToken,
          showPopUpBeforeRemoteMute: false,
          isThisUserAllowedToMuteOthers: userRole === 'mentor' || userRole === 'panelist',
        })
        
        setIsCameraOpen(userHasAllowedVideo)
        setIsAudioOpen(userHasAllowedAudio)
        setTokenLoading(false)
      } else {
        // Token request failed - log error but still allow session to start
        console.error('Failed to get Agora tokens:', {
          responseStatus: response?.status,
          responseCode: response?.data?.code,
          responseScreenStatus: responseScreen?.status,
          responseScreenCode: responseScreen?.data?.code,
        })
        setTokenLoading(false)
      }
      
      // Always try to get chat token (non-blocking)
      try {
        const chatTokenResponse = await getAgoraChatToken()
        if (
          chatTokenResponse?.status === 200 &&
          isCommonResponseSuccessful(chatTokenResponse?.data?.code) &&
          chatTokenResponse?.data?.data?.chatToken
        ) {
          setMyChatToken(chatTokenResponse?.data?.data?.chatToken)
        }
      } catch (error) {
        console.error('Failed to get chat token:', error)
        // Non-blocking - continue even if chat token fails
      }
      
      // Always set session started, even if token requests failed
      // This ensures the button works and user can proceed
      setSessionStarted(true)
      console.log('[handleJoinRoom] Session started for aumhum platform')
      return { success: true, platform: resolvedSelectedPlatform?.id }
    } else {
      // For non-aumhum platforms (offline, phone, etc.)
      setSessionStarted(true)
      console.log(
        '[handleJoinRoom] Session started for non-aumhum platform:',
        resolvedSelectedPlatform?.id,
      )
      return { success: true, platform: resolvedSelectedPlatform?.id }
    }
  }, [])

  const changeLayoutFunction = useCallback(() => {
    setAgoraRtcProps((prev) => ({
      ...prev,
      layout: prev.layout === layout.pin ? layout.grid : layout.pin
    }))
  }, [])

  const joinAgoraCallFunction = useCallback((
    bookedEventFinalData,
    userRole,
    setJoinAgoraCall,
    isCameraOpen,
    isAudioOpen
  ) => {
    markBookedEventLog(bookedEventFinalData?.id, 'JOIN_AGORA_CALL', userRole, {
      camera: isCameraOpen,
      mic: isAudioOpen,
    })
    setJoinAgoraCall(true)
  }, [])

  return {
    // State
    agoraRtcProps,
    setAgoraRtcProps,
    agoraRtmProps,
    setAgoraRtmProps,
    uidObject,
    setUidObject,
    
    // Chat
    myChatId,
    setMyChatId,
    myChatToken,
    setMyChatToken,
    peerChatId,
    setPeerChatId,
    peerChatName,
    setPeerChatName,
    peerChatImage,
    setPeerChatImage,
    
    // Methods
    initializeUidObject,
    handleJoinRoom,
    changeLayoutFunction,
    joinAgoraCallFunction,
  }
}
